// SAD-458 SRC-07: refresh dedup and origin-adoption isolation. Proves that
// re-observing the same disagreement bumps one exception's seen count rather
// than stacking alerts, and that accepting a newer origin value appends a new
// adopted copy — superseding, never overwriting, the one a consumer already
// has.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useRef, useState } from 'react';
import { useObservationConflicts } from './conflictHooks';
import { isPermissionDenied, useAcceptNewerOrigin, useAdoptOriginValue, useObservationExceptions, useOriginAdoptions, useRefreshSources } from './sourceRefreshHooks';
function fmtTime(ts) {
    return ts ? ts.toDate().toLocaleString() : '—';
}
function period(start, end) {
    if (!start && !end) return 'unspecified period';
    return `${start || '…'} → ${end || '…'}`;
}
function entityTitle(e) {
    return [
        e.field,
        e.scope && `scope ${e.scope}`,
        e.measure && `measure ${e.measure}`
    ].filter(Boolean).join(' · ');
}
function seenText(e) {
    const times = e.seenCount === 1 ? '1 time' : `${e.seenCount} times`;
    return `seen ${times} since ${fmtTime(e.firstSeenAt)}`;
}
export function SourceRefreshPanel({ nodeId, nodeName, consumerKind = '', consumerId = '' }) {
    const isPursuit = consumerKind === 'capture_project' && !!consumerId;
    const evidence = useObservationConflicts(nodeId, isPursuit);
    const exceptions = useObservationExceptions(nodeId);
    const adoptions = useOriginAdoptions(nodeId, consumerKind, consumerId);
    const adopt = useAdoptOriginValue();
    const adopting = useRef(false);
    const refresh = useRefreshSources();
    const refreshing = useRef(false);
    function refreshSources() {
        if (refreshing.current) return;
        refreshing.current = true;
        refresh.mutate({
            nodeId
        }, {
            onSettled: ()=>{
                refreshing.current = false;
            }
        });
    }
    if (isPermissionDenied(exceptions.error) || isPermissionDenied(adoptions.error) || isPermissionDenied(refresh.error) || isPursuit && isPermissionDenied(evidence.error) || isPermissionDenied(adopt.error)) {
        return /*#__PURE__*/ _jsx("p", {
            className: "text-xs text-muted-foreground",
            "data-testid": Testids.sourceRefresh.accessDenied,
            children: "You don't have access to source refresh data for this entity."
        });
    }
    const hasExceptions = (exceptions.data?.length ?? 0) > 0;
    const hasAdoptions = (adoptions.data?.length ?? 0) > 0;
    const amounts = (evidence.data ?? []).filter((e)=>e.field === 'amount' && e.observations.some((o)=>o.id === e.preferredObservationId) && !adoptions.data?.some((a)=>a.field === e.field && a.scope === e.scope && a.measure === e.measure && a.periodStart === e.periodStart && a.periodEnd === e.periodEnd));
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-3",
        "data-testid": Testids.sourceRefresh.panelRoot,
        children: [
            /*#__PURE__*/ _jsx(SpecraButton, {
                size: "sm",
                variant: "outline",
                "data-testid": Testids.sourceRefresh.refreshButton,
                disabled: refresh.isPending || adopt.isPending || exceptions.isPending || adoptions.isPending,
                onClick: refreshSources,
                children: refresh.isPending ? 'Refreshing sources…' : 'Refresh sources'
            }),
            /*#__PURE__*/ _jsxs("p", {
                className: "text-xs text-muted-foreground",
                children: [
                    "Check the latest available source evidence for ",
                    nodeName,
                    ". Saved copies stay unchanged."
                ]
            }),
            refresh.isPending && /*#__PURE__*/ _jsx("output", {
                className: "block",
                children: "Refreshing source evidence…"
            }),
            refresh.isError && /*#__PURE__*/ _jsxs("p", {
                role: "alert",
                children: [
                    refresh.error.message,
                    " Retry with Refresh sources."
                ]
            }),
            refresh.isSuccess && /*#__PURE__*/ _jsx("output", {
                className: "block",
                children: "Source evidence refreshed."
            }),
            (exceptions.isPending || adoptions.isPending || isPursuit && evidence.isPending) && /*#__PURE__*/ _jsx("output", {
                className: "block",
                children: "Loading source data…"
            }),
            (exceptions.isError || adoptions.isError || isPursuit && evidence.isError) && /*#__PURE__*/ _jsxs("div", {
                role: "alert",
                children: [
                    "Source refresh data could not be loaded.",
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "ghost",
                        onClick: ()=>{
                            void exceptions.refetch();
                            void adoptions.refetch();
                            if (isPursuit) void evidence.refetch();
                        },
                        children: "Retry loading source data"
                    })
                ]
            }),
            isPursuit && evidence.isSuccess && !evidence.isFetching && amounts.length === 0 && /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: hasAdoptions ? 'Available amounts are already saved.' : 'No source amount is available to save.'
            }),
            isPursuit && !evidence.isError && amounts.map((e)=>/*#__PURE__*/ _jsxs("div", {
                    className: "space-y-2 text-sm",
                    children: [
                        /*#__PURE__*/ _jsxs("p", {
                            children: [
                                entityTitle(e),
                                " · ",
                                period(e.periodStart, e.periodEnd),
                                ": ",
                                e.preferredValue
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            size: "sm",
                            variant: "outline",
                            disabled: adopt.isPending || refresh.isPending || evidence.isFetching || adoptions.isPending || adoptions.isError,
                            onClick: ()=>{
                                if (adopting.current) return;
                                adopting.current = true;
                                adopt.mutate({
                                    nodeId,
                                    field: e.field,
                                    scope: e.scope,
                                    measure: e.measure,
                                    periodStart: e.periodStart,
                                    periodEnd: e.periodEnd,
                                    consumerKind,
                                    consumerId
                                }, {
                                    onSettled: ()=>{
                                        adopting.current = false;
                                    }
                                });
                            },
                            children: "Adopt amount into pursuit"
                        })
                    ]
                }, JSON.stringify([
                    e.field,
                    e.scope,
                    e.measure,
                    e.periodStart,
                    e.periodEnd
                ]))),
            adopt.isPending && /*#__PURE__*/ _jsx("output", {
                className: "block",
                children: "Adopting source amount…"
            }),
            adopt.isError && /*#__PURE__*/ _jsxs("p", {
                role: "alert",
                children: [
                    adopt.error.message,
                    " Retry with Adopt amount into pursuit."
                ]
            }),
            hasExceptions && /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-xs font-medium text-muted-foreground",
                        children: "Source exceptions"
                    }),
                    exceptions.data?.map((e)=>/*#__PURE__*/ _jsx(ExceptionRow, {
                            exception: e
                        }, e.id))
                ]
            }),
            hasAdoptions && /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-xs font-medium text-muted-foreground",
                        children: "Saved copies"
                    }),
                    adoptions.data?.map((a)=>/*#__PURE__*/ _jsx(AdoptionRow, {
                            adoption: a,
                            nodeId: nodeId,
                            nodeName: nodeName
                        }, a.id))
                ]
            }),
            isPursuit && /*#__PURE__*/ _jsx(LegacyClinAdoptions, {
                nodeId: nodeId,
                nodeName: nodeName
            })
        ]
    });
}
function LegacyClinAdoptions({ nodeId, nodeName }) {
    const adoptions = useOriginAdoptions(nodeId, 'pursuit_clin');
    const copies = adoptions.data?.filter((a)=>a.consumerKind === 'pursuit_clin') ?? [];
    if (isPermissionDenied(adoptions.error)) return null;
    if (adoptions.isError) {
        return /*#__PURE__*/ _jsxs("div", {
            role: "alert",
            children: [
                "CLIN saved copies could not be loaded.",
                /*#__PURE__*/ _jsx(SpecraButton, {
                    size: "sm",
                    variant: "ghost",
                    onClick: ()=>void adoptions.refetch(),
                    children: "Retry loading CLIN copies"
                })
            ]
        });
    }
    if (copies.length === 0) return null;
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2",
        children: [
            /*#__PURE__*/ _jsx("p", {
                className: "text-xs font-medium text-muted-foreground",
                children: "CLIN saved copies"
            }),
            copies.map((a)=>/*#__PURE__*/ _jsx(AdoptionRow, {
                    adoption: a,
                    nodeId: nodeId,
                    nodeName: nodeName
                }, a.id))
        ]
    });
}
function ExceptionRow({ exception: e }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-1 rounded-md border bg-muted/20 p-3 text-sm",
        "data-testid": Testids.sourceRefresh.exceptionRow,
        "data-fingerprint": e.fingerprint,
        "data-status": e.status,
        "data-seen-count": e.seenCount,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between gap-2",
                children: [
                    /*#__PURE__*/ _jsxs("span", {
                        className: "font-medium",
                        children: [
                            entityTitle(e),
                            ' ',
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-xs font-normal text-muted-foreground",
                                children: period(e.periodStart, e.periodEnd)
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: e.status === 'open' ? 'outline' : 'secondary',
                        children: e.status
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-xs",
                "data-testid": Testids.sourceRefresh.exceptionEvidence,
                children: e.evidence
            }),
            /*#__PURE__*/ _jsxs("p", {
                className: "text-xs text-muted-foreground",
                children: [
                    seenText(e),
                    " · last seen ",
                    fmtTime(e.lastSeenAt)
                ]
            })
        ]
    });
}
function AdoptionRow({ adoption: a, nodeId, nodeName }) {
    const accept = useAcceptNewerOrigin();
    const [confirming, setConfirming] = useState(false);
    const where = `${nodeName} · ${entityTitle(a)} · ${period(a.periodStart, a.periodEnd)}`;
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2 rounded-md border bg-muted/20 p-3 text-sm",
        "data-testid": Testids.sourceRefresh.adoptionRow,
        "data-adoption-id": a.id,
        "data-newer-origin": a.newerOriginAvailable ? 'true' : 'false',
        children: [
            /*#__PURE__*/ _jsxs("p", {
                className: "text-xs",
                children: [
                    "Saved:",
                    ' ',
                    /*#__PURE__*/ _jsx("span", {
                        className: "font-medium",
                        "data-testid": Testids.sourceRefresh.adoptedValue,
                        children: a.adoptedValue
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("p", {
                className: "text-xs text-muted-foreground",
                "data-testid": Testids.sourceRefresh.adoptionProvenance,
                children: [
                    a.originSource,
                    " · record ",
                    a.originSourceRecordId,
                    a.originSourceRecordVersion ? ` version ${a.originSourceRecordVersion}` : '',
                    " · saved",
                    ' ',
                    fmtTime(a.adoptedAt),
                    " by ",
                    a.actorUserId
                ]
            }),
            a.newerOriginAvailable && !confirming && /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2 rounded-md border bg-background p-2",
                children: [
                    /*#__PURE__*/ _jsxs("p", {
                        className: "text-xs",
                        "data-testid": Testids.sourceRefresh.newerOrigin,
                        "data-origin-value": a.originCurrentValue,
                        children: [
                            "Newer origin available: ",
                            /*#__PURE__*/ _jsx("span", {
                                className: "font-medium",
                                children: a.originCurrentValue
                            }),
                            " (",
                            a.originCurrentSource,
                            ")"
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "outline",
                        "data-testid": Testids.sourceRefresh.acceptButton,
                        onClick: ()=>setConfirming(true),
                        children: "Accept"
                    })
                ]
            }),
            confirming && /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2 rounded-md border bg-background p-2",
                "data-testid": Testids.sourceRefresh.acceptConfirmPanel,
                children: [
                    /*#__PURE__*/ _jsxs("p", {
                        className: "text-xs",
                        children: [
                            "For ",
                            where,
                            ', the saved copy ("',
                            a.adoptedValue,
                            '") is kept as a superseded record and a new saved copy is written with "',
                            a.originCurrentValue,
                            '" from ',
                            a.originCurrentSource,
                            ". The stored copy is never changed in place."
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                disabled: accept.isPending,
                                "data-testid": Testids.sourceRefresh.acceptConfirmButton,
                                onClick: ()=>accept.mutate({
                                        nodeId,
                                        adoptionId: a.id
                                    }, {
                                        onSuccess: ()=>setConfirming(false)
                                    }),
                                children: "Confirm acceptance"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                onClick: ()=>setConfirming(false),
                                children: "Back"
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
